import type { ProcessorUpgradeFeature } from '@sb/service-interfaces/host/types';

import { LOCATE_METHOD_LABELS } from './locateMethodLabels';
import type { LocateSkillMethod } from './types';

/**
 * The learned locate methods — those needing more compute than a low-tier
 * control box has. The one answer every surface reads, so a locked picker row
 * and a refused routine cannot name different things.
 */
export interface IntensiveLocateMethod {
  label: string;
  feature: ProcessorUpgradeFeature;
  /** The inline `Locate` step spells the same detector differently. */
  stepMethodKind: string;
}

const INTENSIVE_LOCATE_METHODS: Readonly<
  Partial<Record<LocateSkillMethod, IntensiveLocateMethod>>
> = {
  dino_fast: {
    label: LOCATE_METHOD_LABELS.dino_fast,
    feature: 'CLICK_FIND',
    stepMethodKind: 'DinoFast',
  },
  text_prompt: {
    label: LOCATE_METHOD_LABELS.text_prompt,
    feature: 'TEXT_FIND',
    stepMethodKind: 'TextPromptDetection2D',
  },
  custom_checkpoint: {
    label: LOCATE_METHOD_LABELS.custom_checkpoint,
    feature: 'CUSTOM_MODEL',
    stepMethodKind: 'CustomCheckpointDetection2D',
  },
};

/** Undefined when the method runs on any control box. */
export function intensiveLocateMethod(
  method: string | null | undefined,
): IntensiveLocateMethod | undefined {
  if (!method) return undefined;

  return INTENSIVE_LOCATE_METHODS[method as LocateSkillMethod];
}

/** The same, keyed by the inline `Locate` step's method kind. */
export function intensiveLocateMethodKind(
  kind: string | null | undefined,
): IntensiveLocateMethod | undefined {
  if (!kind) return undefined;

  return Object.values(INTENSIVE_LOCATE_METHODS).find(
    (entry) => entry.stepMethodKind === kind,
  );
}
